import { zodResolver } from "@hookform/resolvers/zod";
import type { AssignableRole, MembershipRole } from "@unbox/api";
import { merchantScopeRoles, workspaceScopeRoles } from "@unbox/api";
import { ChevronDown, ChevronUp, Mail01 } from "@untitledui/icons";
import { useEffect, useRef, useState } from "react";
import { DialogTrigger, Heading } from "react-aria-components";
import { Controller, useForm } from "react-hook-form";
import z from "zod/v3";
import type { ScopedRoleValue } from "@/components/admin/role-label";
import { MerchantLogo } from "@/components/application/merchant-logo/merchant-logo";
import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { Button } from "@/components/base/buttons/button";
import { CloseButton } from "@/components/base/buttons/close-button";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { Input } from "@/components/base/input/input";
import { cx } from "@/utils/cx";
import { TeamRoleSelect } from "./team-roles";

const brandGrantSchema = z.object({
  merchantId: z.string(),
  role: z.enum(["user", "merchant_admin"]),
});

const schema = z
  .object({
    email: z.string().trim().min(1, "Email is required").email("Enter a valid email address"),
    workspaceAdmin: z.boolean(),
    brands: z.array(brandGrantSchema),
  })
  .refine((values) => values.workspaceAdmin || values.brands.length > 0, {
    message: "Select at least one brand",
    path: ["brands"],
  });

export type WorkspaceMemberFormValues = z.infer<typeof schema>;

export type WorkspaceMemberBrand = { id: string; name: string; logoUrl?: string | null };

type WorkspaceMemberModalProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  workspace: { name: string; logoUrl?: string | null; brands: WorkspaceMemberBrand[] };
  /** Roles the viewer may grant — `usePermissions().invitableRoles`. */
  assignableRoles: AssignableRole[];
  editing?: WorkspaceMemberFormValues | null;
  onSubmit: (values: WorkspaceMemberFormValues) => void;
  isPending?: boolean;
};

const blank: WorkspaceMemberFormValues = { email: "", workspaceAdmin: false, brands: [] };

export const WorkspaceMemberModal = ({
  isOpen,
  onOpenChange,
  workspace,
  assignableRoles,
  editing,
  onSubmit,
  isPending,
}: WorkspaceMemberModalProps) => {
  const isEditing = !!editing;
  const [isExpanded, setIsExpanded] = useState(true);

  const form = useForm<WorkspaceMemberFormValues>({ resolver: zodResolver(schema), defaultValues: blank });

  // Seed the form only as the modal opens. The parent rebuilds `editing` on every
  // render, so resetting whenever its reference changes would wipe an in-progress
  // edit on any unrelated re-render (e.g. the team list refetching) — ticking a
  // second brand and having the form clear.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      form.reset(editing ?? blank);
      setIsExpanded(true);
    }
    wasOpen.current = isOpen;
  }, [isOpen, editing, form]);

  const workspaceAdmin = form.watch("workspaceAdmin");
  const brands = form.watch("brands");
  const brandsError = form.formState.errors.brands?.message;

  const grantedRole = (merchantId: string): MembershipRole | undefined =>
    brands.find((brand) => brand.merchantId === merchantId)?.role;

  const setBrands = (next: WorkspaceMemberFormValues["brands"]) =>
    form.setValue("brands", next, { shouldValidate: form.formState.isSubmitted });

  const toggleBrand = (merchantId: string) =>
    setBrands(
      grantedRole(merchantId)
        ? brands.filter((brand) => brand.merchantId !== merchantId)
        : [...brands, { merchantId, role: "user" as const }],
    );

  const setBrandRole = (merchantId: string, role: MembershipRole) =>
    setBrands(brands.map((brand) => (brand.merchantId === merchantId ? { ...brand, role } : brand)));

  const workspaceRole = ((): ScopedRoleValue => {
    if (workspaceAdmin) return "workspace_admin";
    if (brands.length === 0) return "user";
    if (brands.every((brand) => brand.role === brands[0].role)) return brands[0].role;
    return "multiple";
  })();

  const setWorkspaceRole = (role: AssignableRole) => {
    if (role === "workspace_admin") {
      form.setValue("workspaceAdmin", true, { shouldValidate: form.formState.isSubmitted });
      return;
    }

    form.setValue("workspaceAdmin", false);
    const targets = brands.length > 0 ? brands.map((brand) => brand.merchantId) : workspace.brands.map((b) => b.id);
    setBrands(targets.map((merchantId) => ({ merchantId, role })));
  };

  const selectedCount = workspaceAdmin ? workspace.brands.length : brands.length;

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
      {/* Not dismissable: an outside click would discard the whole form (UNB-2129). */}
      <ModalOverlay isDismissable={false}>
        {/* 640px matches the design and the other invite modals; the base Modal is
            unconstrained, so without a cap it stretches to the overlay. */}
        <Modal className="max-w-[640px]">
          <Dialog>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="relative w-full overflow-hidden rounded-2xl bg-primary shadow-xl transition-all"
            >
              <CloseButton
                onClick={() => onOpenChange(false)}
                theme="light"
                size="lg"
                className="absolute top-3 right-3"
              />
              <div className="flex flex-col gap-4 px-4 pt-5 mr-4 sm:px-6 sm:pt-6">
                <div className="z-10 flex flex-col gap-0.5">
                  <Heading slot="title" className="text-md font-semibold text-primary">
                    {isEditing ? "Edit team member" : "Add team member"}
                  </Heading>
                  <p className="text-sm text-tertiary">
                    {isEditing
                      ? "Change what this person can access."
                      : "Invite someone and configure what they can access."}
                  </p>
                </div>
              </div>

              <div className="h-5 w-full" />

              <div className="relative flex flex-col gap-4 px-4 sm:gap-5 sm:px-6">
                <Controller
                  control={form.control}
                  name="email"
                  render={({ field, fieldState }) => (
                    <Input
                      isRequired
                      size="md"
                      label="Email"
                      placeholder="Email"
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      isDisabled={isEditing}
                      isInvalid={!!fieldState.error}
                      hint={fieldState.error?.message}
                    />
                  )}
                />

                <div className="flex flex-col gap-1.5">
                  <span className="text-sm font-semibold text-secondary">Workspace</span>

                  <div className="flex flex-col gap-2 rounded-lg bg-tertiary px-4 py-2">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex min-w-0 flex-1 items-center gap-3">
                        {/* Everyone added here belongs to the workspace being viewed;
                            the row is shown for context, not as a choice. */}
                        <Checkbox size="md" isSelected isDisabled aria-label={workspace.name} />
                        <MerchantLogo
                          name={workspace.name}
                          logoUrl={workspace.logoUrl}
                          size="xs"
                          className="size-8 rounded-md"
                        />
                        <span className="truncate text-sm font-semibold text-primary">{workspace.name}</span>
                        <span className="shrink-0 text-sm text-brand-secondary">
                          {selectedCount} of {workspace.brands.length} Merchants
                        </span>
                        <TeamRoleSelect
                          aria-label={`Role across ${workspace.name}`}
                          options={workspaceScopeRoles}
                          assignableRoles={assignableRoles}
                          value={workspaceRole}
                          onChange={setWorkspaceRole}
                        />
                      </div>
                      <button
                        type="button"
                        aria-label={isExpanded ? "Hide brands" : "Show brands"}
                        onClick={() => setIsExpanded((open) => !open)}
                        className="cursor-pointer text-fg-quaternary"
                      >
                        {isExpanded ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
                      </button>
                    </div>

                    {isExpanded && (
                      <>
                        <div className="h-px w-full bg-border-secondary" />
                        <div className="flex max-h-64 flex-col gap-3 overflow-y-auto py-3">
                          {workspace.brands.length === 0 ? (
                            <p className="text-sm text-tertiary">This workspace has no brands yet.</p>
                          ) : (
                            workspace.brands.map((brand) => {
                              const role = grantedRole(brand.id);
                              return (
                                <div key={brand.id} className="flex items-center justify-between gap-3">
                                  <div className="flex min-w-0 items-center gap-3">
                                    <Checkbox
                                      size="md"
                                      aria-label={brand.name}
                                      // A Workspace Admin already reaches every brand, so
                                      // the per-brand choice has nothing left to decide.
                                      isSelected={workspaceAdmin || !!role}
                                      isDisabled={workspaceAdmin}
                                      onChange={() => toggleBrand(brand.id)}
                                    />
                                    <MerchantLogo
                                      name={brand.name}
                                      logoUrl={brand.logoUrl}
                                      size="xs"
                                      className="size-8 rounded-md"
                                    />
                                    <span className="truncate text-sm font-semibold text-primary">{brand.name}</span>
                                  </div>
                                  <div className={cx(!role && !workspaceAdmin && "opacity-40")}>
                                    <TeamRoleSelect
                                      aria-label={`Role for ${brand.name}`}
                                      options={merchantScopeRoles}
                                      assignableRoles={assignableRoles}
                                      value={workspaceAdmin ? "workspace_admin" : (role ?? "user")}
                                      isDisabled={workspaceAdmin || !role}
                                      onChange={(next) => setBrandRole(brand.id, next as MembershipRole)}
                                    />
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  {brandsError && <span className="text-sm text-error-primary">{brandsError}</span>}
                </div>

                {!isEditing && (
                  <div className="flex items-center gap-1.5 rounded-xl bg-secondary p-4">
                    <Mail01 className="size-5 shrink-0 text-fg-secondary" />
                    <p className="text-sm font-medium text-secondary">
                      This will auto send an email to the user asking them to set up their account.
                    </p>
                  </div>
                )}
              </div>

              <div className="z-10 flex flex-1 flex-col-reverse gap-3 p-4 pt-6 *:grow sm:grid sm:grid-cols-2 sm:px-6 sm:pt-8 sm:pb-6">
                <Button color="secondary" size="lg" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" color="primary" size="lg" isLoading={isPending}>
                  {isEditing ? "Save changes" : "Add new member"}
                </Button>
              </div>
            </form>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </DialogTrigger>
  );
};
